import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { splitQuotedBody } from '@/lib/emailBody';
import { cn } from '@/lib/cn';

/**
 * Renders an email plaintext body with the quoted-reply tail collapsed
 * behind a "Show quoted text" toggle. Uses prose typography so it reads
 * like mail rather than a code block.
 */
export function EmailBody({
  body,
  className,
}: {
  body: string | null | undefined;
  className?: string;
}) {
  const split = splitQuotedBody(body);
  const [expanded, setExpanded] = useState(false);

  if (!split.visible && !split.hasQuoted) {
    return (
      <p className={cn('text-sm italic text-muted-foreground', className)}>(empty body)</p>
    );
  }

  return (
    <div className={cn('text-[15px] leading-[1.6] text-foreground/90', className)}>
      {split.visible && (
        <div className="whitespace-pre-wrap break-words">{split.visible}</div>
      )}
      {split.hasQuoted && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            {expanded ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            {expanded ? 'Hide quoted text' : 'Show quoted text'}
          </button>
          <AnimatePresence initial={false}>
            {expanded && (
              <motion.div
                initial={{ opacity: 0, y: -4, height: 0 }}
                animate={{ opacity: 1, y: 0, height: 'auto' }}
                exit={{ opacity: 0, y: -4, height: 0 }}
                transition={{ type: 'spring', stiffness: 120, damping: 14 }}
                className="overflow-hidden"
              >
                <div className="mt-2 whitespace-pre-wrap break-words border-l-2 border-border pl-3 text-[14px] text-muted-foreground">
                  {split.quoted}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
